import React, { useEffect, useRef } from "react"
import PropTypes from "prop-types" // Import prop-types for validation
import { useAuth0 } from "@auth0/auth0-react"
import { Navigate, useLocation } from "react-router-dom"
import { Loader2 } from "../ui/Icon"
import { useSelector, useDispatch } from "react-redux"
import { fetchAuthToken } from "../../helpers/headerMenu"
import { AuthTokenManager } from "../../helpers/commonUtills"

const PrivateRoute = ({ children, allowedRoles, requiredPermissions }) => {
  const {
    isAuthenticated,
    isLoading,
    user,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0()
  const dispatch = useDispatch()
  const location = useLocation()
  const JwtToken = AuthTokenManager.get()
  // Prevent duplicate redirects while the re-auth navigation is pending.
  const reauthTriggeredRef = useRef(false)

  // Stale Auth0 cache (ID token kept, refresh token gone) leaves isAuthenticated=true
  // but fetchAuthToken fails — force a fresh login and preserve the deep link.
  const handleAuthFailure = () => {
    if (reauthTriggeredRef.current) return
    reauthTriggeredRef.current = true
    const returnTo = location.pathname + location.search
    void loginWithRedirect({
      appState: returnTo && returnTo !== "/" ? { returnTo } : undefined,
    })
  }

  useEffect(() => {
    if (isAuthenticated)
      fetchAuthToken(dispatch, user, getAccessTokenSilently, handleAuthFailure)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user, getAccessTokenSilently, isAuthenticated])

  const roleType = useSelector(
    (state) => state?.rolePermission?.permissions?.role,
  )

  // Gate 2: Wait for role permissions to be populated in Redux
  const rolePermission = useSelector((state) => state.rolePermission)

  // Show a loading state while the Auth0 state is being determined
  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Loader2 className="animate-spin" size={48} />
      </div>
    )
  }

  if (isAuthenticated && JwtToken === null) {
    fetchAuthToken(dispatch, user, getAccessTokenSilently, handleAuthFailure)
  }

  // Gate: wait for JWT and Redux role permissions before rendering children
  if (
    isAuthenticated &&
    (!AuthTokenManager.get() || rolePermission.permissions === null)
  ) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Loader2 className="animate-spin" size={48} />
      </div>
    )
  }

  // If the user is not authenticated, redirect to login and preserve the intended URL
  // so after login we can send them back (e.g. deep link to scheduling-profile?confirmationNumber=...)
  if (!isAuthenticated) {
    const returnTo = location.pathname + location.search
    const loginPath =
      returnTo && returnTo !== "/"
        ? `/?returnTo=${encodeURIComponent(returnTo)}`
        : "/"
    return <Navigate to={loginPath} replace />
  }

  // Check if the user has the required role from the allowedRoles.
  // On mismatch, redirect to /home — the user IS authenticated, just lacks permission.
  // Do NOT log out: that is hostile UX and the prior handleLogout never called Auth0 logout() anyway.
  if (user && allowedRoles && !allowedRoles.includes(roleType)) {
    return <Navigate to="/home" replace />
  }

  // Feature-level permission check (only applies to Facility User).
  // SA and FA always have access to feature-gated sections per business rules.
  if (
    requiredPermissions?.length > 0 &&
    roleType === "Facility User" &&
    !requiredPermissions.every(
      (perm) => rolePermission.permissions?.permissions?.[perm],
    )
  ) {
    return <Navigate to="/home" replace />
  }

  // If the user is authenticated and has the required role(s), render the children (the protected route component)
  return children
}

// Define prop types
PrivateRoute.propTypes = {
  children: PropTypes.node.isRequired, // Validate that children is a valid React node and required
  allowedRoles: PropTypes.arrayOf(PropTypes.string), // Validate that allowedRoles is an array of strings
  requiredPermissions: PropTypes.arrayOf(PropTypes.string), // Feature permission keys (e.g. "schedule_management"); only enforced for Facility User
}

export default PrivateRoute
